import type { IControl, Map as MapLibreMap } from "maplibre-gl";
import { createMapStreetViewButton } from "./map-embed-controls.ts";
import { registerStreetViewMap } from "./street-view/register.ts";

/** Same needle and pin as the quest page map (route-map.ts), so both maps read alike. */
export const COMPASS_NEEDLE_SVG =
  '<svg viewBox="0 0 24 24" width="22" height="22"><path d="M12 2.5 16 12H8z" fill="#e5484d"/><path d="M12 21.5 8 12h8z" fill="#9aa0a6"/><text x="12" y="10.4" text-anchor="middle" font-size="5.2" font-weight="800" fill="#fff">N</text></svg>';
export const PIN_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M9 3h6l-1 6 3.5 3.5V14h-11v-1.5L10 9 9 3Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M12 14v7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

export interface MapViewPaint {
  bearing: number;
  compassShown: boolean;
  compassLabel: string;
  homeShown: boolean;
  homeLabel: string;
}

/**
 * Compass and back-to-start pin for the home map, stacked above the zoom buttons in the
 * bottom-right corner so they move with them (above the bottom panel on small screens).
 */
export class MapViewControls implements IControl {
  private box = document.createElement("div");
  private compass = document.createElement("button");
  private needle = document.createElement("span");
  private home = document.createElement("button");
  private streetView: HTMLButtonElement | null = null;

  constructor(handlers: { onCompass: () => void; onHome: () => void; map?: MapLibreMap }) {
    this.box.className = "maplibregl-ctrl map-view-controls";
    this.compass.type = "button";
    this.compass.className = "route-map-compass map-view-compass";
    this.needle.className = "route-map-compass-needle";
    this.needle.setAttribute("aria-hidden", "true");
    this.needle.innerHTML = COMPASS_NEEDLE_SVG;
    this.compass.appendChild(this.needle);
    this.home.type = "button";
    this.home.className = "route-map-home map-view-home";
    this.home.innerHTML = PIN_ICON_SVG;
    const press = (el: HTMLButtonElement, fn: () => void) =>
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        fn();
      });
    press(this.compass, handlers.onCompass);
    press(this.home, handlers.onHome);
    if (handlers.map) {
      const map = handlers.map;
      this.streetView = createMapStreetViewButton();
      registerStreetViewMap({
        map,
        button: this.streetView,
        getCenter: () => {
          const c = map.getCenter();
          return { lng: c.lng, lat: c.lat };
        },
      });
      this.box.append(this.compass, this.home, this.streetView);
    } else {
      this.box.append(this.compass, this.home);
    }
  }

  onAdd(): HTMLElement {
    return this.box;
  }

  onRemove(): void {
    this.box.remove();
  }

  /** Basemap toggle sits above the compass and pin in the column. */
  prepend(el: HTMLElement): void {
    this.box.insertBefore(el, this.box.firstChild);
  }

  paint(p: MapViewPaint): void {
    this.needle.style.transform = `rotate(${-p.bearing}deg)`;
    this.compass.hidden = !p.compassShown;
    this.compass.title = p.compassLabel;
    this.compass.setAttribute("aria-label", p.compassLabel);
    this.home.hidden = !p.homeShown;
    this.home.title = p.homeLabel;
    this.home.setAttribute("aria-label", p.homeLabel);
    const basemap = this.box.querySelector(".map-basemap-toggle");
    const basemapOn = basemap instanceof HTMLElement && !basemap.hidden;
    this.box.hidden = !p.compassShown && !p.homeShown && !basemapOn;
  }
}
